/**
 * UsersManagementView
 * Gestión de usuarios administradores y permisos granulares.
 */

import { useState, useEffect } from "react";
import { useAuth } from "../auth-context";
import {
    Shield,
    Users,
    UserPlus,
    Search,
    Pencil,
    Trash2,
    Lock,
    Settings,
    BarChart3,
    GraduationCap,
    UserCheck,
    UserX,
    Calendar,
    Loader2,
    Plus,
    Eye,
    EyeOff,
    AlertTriangle,
    Database,
    ShieldCheck,
    Mail as MailIcon,
    CheckCircle2,
} from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription
} from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
} from "../ui/sheet";
import { Alert, AlertDescription } from "../ui/alert";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import api from "../../src/api";
import { toast } from "sonner";
import { Label } from "../ui/label";
import { GLOBAL_DELEGABLE_PERMISSIONS, PERMISSIONS_BY_MODULE, ROLE_PRESETS as SHARED_PRESETS } from "../../../shared/permissions";


// --- CONSTANTS ---

// Mapeo de iconos para las categorías compartidas
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
    registros: <Users className="h-4 w-4" />,
    academico: <GraduationCap className="h-4 w-4" />,
    administracion: <Settings className="h-4 w-4" />,
    reportes: <BarChart3 className="h-4 w-4" />,
};

const PERMISSION_CATEGORIES = PERMISSIONS_BY_MODULE.map(mod => ({
    id: mod.id,
    name: mod.label,
    icon: CATEGORY_ICONS[mod.id] || <Shield className="h-4 w-4" />,
    permissions: mod.permissions.map(p => ({
        id: p.id,
        name: p.label,
        desc: p.description
    }))
}));

const GLOBAL_PERMISSION_IDS = new Set<string>(GLOBAL_DELEGABLE_PERMISSIONS as readonly string[]);
const GLOBAL_PERMISSION_CATEGORIES = PERMISSION_CATEGORIES
    .map(cat => ({
        ...cat,
        permissions: cat.permissions.filter(permission => GLOBAL_PERMISSION_IDS.has(permission.id)),
    }))
    .filter(cat => cat.permissions.length > 0);

const ROLE_PRESETS = [
    { id: "custom", name: "Personalizado", perms: [] as string[] },
    ...Object.entries(SHARED_PRESETS).map(([name, perms]) => ({
        id: name.toLowerCase().replace(/ /g, "_"),
        name: name,
        perms: perms as unknown as string[]
    }))
];



interface AdminUser {
    id: string;
    email: string;
    firstName: string | null;
    fatherLastName: string | null;
    motherLastName: string | null;
    name: string;
    authProvider: string;
    isSuperAdmin: boolean;
    isActive: boolean;
    lastLoginAt: string | null;
    createdAt: string;
    globalPermissions?: string[];
    _count: {
        assignedEvents: number;
    };
}

interface Assignment {
    adminId: string;
    eventId: string;
    roleName: string;
    permissions: string[];
    event: {
        id: string;
        name: string;
    };
}

interface Event {
    id: string;
    name: string;
}

interface CleanupDiagnosis {
    user: { id: string; name: string | null; emailMasked: string; isActive: boolean };
    allowlisted: boolean;
    riskLevel: 'BLOCKED' | 'SOFT_DELETE_ONLY' | 'CLEANUP_CANDIDATE';
    cleanupEligibility: 'BLOCKED' | 'SOFT_DELETE_ONLY' | 'CLEANUP_CANDIDATE';
    hardDeleteBlocked: boolean;
    canDeactivate: boolean;
    canHardDelete: boolean;
    hardDeleteBlockers: string[];
    dependencies: Record<string, number>;
    mailEligibility: { canReceiveOperationalEmails: boolean; reason: string };
    recommendation: string;
}

interface CleanupDryRun {
    success: boolean;
    mode: string;
    canApply: boolean;
    riskLevel: string;
    allowlisted: boolean;
    hardDeleteBlockers: string[];
    plan: Array<{ entity: string; action: string; count: number }>;
}

interface CleanupApplyResult {
    success: boolean;
    mode: string;
    deletedCounts: Record<string, number>;
    totalDeleted: number;
    hardDeleteBlockers: string[];
}

export function UsersManagementView({
    events,
    onPermissionsUpdated
}: {
    events: Event[],
    onPermissionsUpdated?: () => void
}) {
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const { user: currentUser } = useAuth();
    const isDeveloper = currentUser?.email?.toLowerCase() === 'victor.gutierrez@unach.mx';
    const isSuperAdmin = Boolean(currentUser?.isSuperAdmin);

    // MODALS
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [isAssignmentsOpen, setIsAssignmentsOpen] = useState(false);
    const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
    const [globalPermissions, setGlobalPermissions] = useState<string[]>([]);
    const [isSavingGlobalPermissions, setIsSavingGlobalPermissions] = useState(false);

    // REGISTRATION STATE
    const [isRegisterOpen, setIsRegisterOpen] = useState(false);
    const [searchEmail, setSearchEmail] = useState("");
    const [isSearchingLdap, setIsSearchingLdap] = useState(false);
    const [ldapQueryResult, setLdapQueryResult] = useState<any>(null);
    const [registerAsSuperAdmin, setRegisterAsSuperAdmin] = useState(false);
    const [regAuthProvider, setRegAuthProvider] = useState<string>("LDAP");
    const [localPass, setLocalPass] = useState("");
    const [localNames, setLocalNames] = useState({ first: "", father: "", mother: "" });

    // EDIT STATE
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        id: "",
        email: "",
        firstName: "",
        fatherLastName: "",
        motherLastName: "",
        password: "",
        isSuperAdmin: false
    });

    const [showPassReg, setShowPassReg] = useState(false);
    const [showPassEdit, setShowPassEdit] = useState(false);

    // CLEANUP DIAGNOSTIC STATE
    const [diagnosisTarget, setDiagnosisTarget] = useState<AdminUser | null>(null);
    const [diagnosis, setDiagnosis] = useState<CleanupDiagnosis | null>(null);
    const [isDiagnosisOpen, setIsDiagnosisOpen] = useState(false);
    const [isLoadingDiagnosis, setIsLoadingDiagnosis] = useState(false);
    const [dryRun, setDryRun] = useState<CleanupDryRun | null>(null);
    const [isDryRunOpen, setIsDryRunOpen] = useState(false);
    const [isConfirmCleanupOpen, setIsConfirmCleanupOpen] = useState(false);
    const [cleanupConfirmation, setCleanupConfirmation] = useState("");
    const [cleanupResult, setCleanupResult] = useState<CleanupApplyResult | null>(null);
    const [isCleanupResultOpen, setIsCleanupResultOpen] = useState(false);
    const [isCleanupLoading, setIsCleanupLoading] = useState(false);

    // NEW ASSIGNMENT STATE
    const [isAddingAssignment, setIsAddingAssignment] = useState(false);
    const [newAssignment, setNewAssignment] = useState({
        eventIds: [] as string[],
        roleName: "gestor_de_registro", // ID normalizado según shared/permissions
        permissions: (SHARED_PRESETS['Gestor de Registro'] as unknown as string[]) || []
    });



    const handleApplyPreset = (roleId: string) => {
        const preset = ROLE_PRESETS.find(r => r.id === roleId);
        if (preset && preset.perms) {
            setNewAssignment(prev => ({
                ...prev,
                roleName: roleId,
                permissions: [...preset.perms]
            }));
        } else {
            setNewAssignment(prev => ({ ...prev, roleName: "custom" }));
        }
    };

    const togglePermission = (permId: string) => {
        setNewAssignment(prev => {
            const hasPerm = prev.permissions.includes(permId);
            const newPerms = hasPerm
                ? prev.permissions.filter(p => p !== permId)
                : [...prev.permissions, permId];

            return {
                ...prev,
                permissions: newPerms,
                roleName: "custom"
            };
        });
    };

    const toggleGlobalPermission = (permId: string) => {
        if (!GLOBAL_PERMISSION_IDS.has(permId)) return;
        setGlobalPermissions(prev => (
            prev.includes(permId)
                ? prev.filter(permission => permission !== permId)
                : [...prev, permId]
        ));
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin-users");
            setUsers(data);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("No se pudieron cargar los usuarios.");
        } finally {
            setLoading(false);
        }
    };

    const handleSearchLdap = async () => {
        if (!searchEmail.includes("@unach.mx")) {
            toast.error("Ingrese un correo institucional válido (@unach.mx)");
            return;
        }
        setIsSearchingLdap(true);
        setLdapQueryResult(null);
        try {
            const { data } = await api.get(`/admin-users/search`, { params: { email: searchEmail } });
            setLdapQueryResult(data);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Usuario no encontrado en el LDAP.");
        } finally {
            setIsSearchingLdap(false);
        }
    };

    const handleRegisterUser = async () => {
        if (regAuthProvider === "LDAP" && !ldapQueryResult) return;
        if (regAuthProvider === "LOCAL" && (!searchEmail || !localPass || !localNames.first)) {
            toast.error("Complete todos los campos para el usuario local.");
            return;
        }

        try {
            const body = regAuthProvider === "LDAP" ? {
                email: ldapQueryResult.email,
                isSuperAdmin: registerAsSuperAdmin,
                authProvider: "LDAP"
            } : {
                email: searchEmail,
                password: localPass,
                firstName: localNames.first,
                fatherLastName: localNames.father,
                motherLastName: localNames.mother,
                isSuperAdmin: registerAsSuperAdmin,
                authProvider: "LOCAL"
            };

            await api.post("/admin-users", body);
            toast.success("Usuario registrado correctamente");
            setIsRegisterOpen(false);
            setLdapQueryResult(null);
            setSearchEmail("");
            setLocalPass("");
            setLocalNames({ first: "", father: "", mother: "" });
            fetchUsers();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error al registrar usuario.");
        }
    };

    const handleEditUser = async () => {
        try {
            const payload = { ...editForm };
            if (!payload.password) delete (payload as any).password;

            await api.patch(`/admin-users/${editForm.id}`, payload);
            toast.success("Usuario actualizado correctamente");
            setIsEditOpen(false);
            fetchUsers();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error al actualizar usuario.");
        }
    };

    const openEdit = (user: AdminUser) => {
        setEditForm({
            id: user.id,
            email: user.email,
            firstName: user.firstName || "",
            fatherLastName: user.fatherLastName || "",
            motherLastName: user.motherLastName || "",
            password: "",
            isSuperAdmin: user.isSuperAdmin
        });
        setIsEditOpen(true);
    };

    const handleToggleStatus = async (user: AdminUser) => {
        try {
            await api.patch(`/admin-users/${user.id}`, { isActive: !user.isActive });
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, isActive: !u.isActive } : u));
            toast.success(user.isActive ? "Usuario desactivado" : "Usuario activado");
        } catch (error) {
            toast.error("Error al cambiar estado");
        }
    };

    const handleDeleteUser = async (user: AdminUser) => {
        if (!window.confirm(`¿Está seguro de eliminar a ${user.name}? Esta acción no se puede deshacer si el usuario no tiene registros vinculados.`)) return;

        try {
            await api.delete(`/admin-users/${user.id}`);
            toast.success("Usuario eliminado del sistema");
            setUsers(prev => prev.filter(u => u.id !== user.id));
        } catch (error: any) {
            const message = error.response?.data?.message || "Error al eliminar usuario.";
            if (error.response?.status === 400 || error.response?.status === 409) {
                toast.error("No se puede eliminar el usuario porque tiene registros asociados.", {
                    description: "Revisa sus dependencias o desactívalo.",
                    action: {
                        label: "Ver dependencias",
                        onClick: () => openDependencies(user),
                    },
                });
                return;
            }
            toast.error(message);
        }
    };

    const openDependencies = async (user: AdminUser) => {
        if (!isSuperAdmin) return;
        setDiagnosisTarget(user);
        setIsDiagnosisOpen(true);
        setIsLoadingDiagnosis(true);
        setDiagnosis(null);
        try {
            const { data } = await api.get<CleanupDiagnosis>(`/admin/maintenance/users/${user.id}/dependencies`, {
                headers: { "X-Passive-Request": "true" },
            });
            setDiagnosis(data);
        } catch {
            toast.error("No fue posible cargar el diagnóstico de dependencias.");
        } finally {
            setIsLoadingDiagnosis(false);
        }
    };

    const deactivateFromDiagnosis = async () => {
        if (!diagnosisTarget) return;
        setIsCleanupLoading(true);
        try {
            await api.post(`/admin/maintenance/users/${diagnosisTarget.id}/deactivate`, {});
            toast.success("Usuario desactivado correctamente.");
            setUsers(prev => prev.map(u => u.id === diagnosisTarget.id ? { ...u, isActive: false } : u));
            await openDependencies({ ...diagnosisTarget, isActive: false });
        } catch {
            toast.error("No fue posible desactivar el usuario.");
        } finally {
            setIsCleanupLoading(false);
        }
    };

    const runDryRun = async () => {
        if (!diagnosisTarget) return;
        setIsCleanupLoading(true);
        try {
            const { data } = await api.post<CleanupDryRun>(`/admin/maintenance/users/${diagnosisTarget.id}/smoke-cleanup`, { dryRun: true });
            setDryRun(data);
            setIsDryRunOpen(true);
        } catch {
            toast.error("No fue posible simular la limpieza.");
        } finally {
            setIsCleanupLoading(false);
        }
    };

    const applyCleanup = async () => {
        if (!diagnosisTarget || cleanupConfirmation !== "LIMPIAR PRUEBA") return;
        setIsCleanupLoading(true);
        try {
            const { data } = await api.post<CleanupApplyResult>(`/admin/maintenance/users/${diagnosisTarget.id}/smoke-cleanup`, {
                dryRun: false,
                confirmation: cleanupConfirmation,
            });
            setCleanupResult(data);
            setIsConfirmCleanupOpen(false);
            setIsDryRunOpen(false);
            setIsDiagnosisOpen(false);
            setIsCleanupResultOpen(true);
            setUsers(prev => prev.filter(u => u.id !== diagnosisTarget.id));
        } catch (error: any) {
            if (error.response?.status === 409) {
                toast.error("Limpieza bloqueada.", {
                    description: "El usuario ya no cumple los criterios de limpieza.",
                });
                return;
            }
            toast.error("No fue posible aplicar la limpieza controlada.");
        } finally {
            setIsCleanupLoading(false);
        }
    };

    const openAssignments = async (user: AdminUser) => {
        setSelectedUser(user);
        setIsAssignmentsOpen(true);
        setIsLoadingAssignments(true);
        setGlobalPermissions([]);
        try {
            const [assignmentsResponse, globalPermissionsResponse] = await Promise.all([
                api.get(`/admin-users/${user.id}/assignments`),
                api.get(`/admin-users/${user.id}/global-permissions`),
            ]);
            setAssignments(assignmentsResponse.data);
            setGlobalPermissions(Array.isArray(globalPermissionsResponse.data?.globalPermissions)
                ? globalPermissionsResponse.data.globalPermissions
                : []);
        } catch (error) {
            toast.error("Error al cargar permisos");
        } finally {
            setIsLoadingAssignments(false);
        }
    };

    const handleSaveGlobalPermissions = async () => {
        if (!selectedUser) return;
        setIsSavingGlobalPermissions(true);
        try {
            const { data } = await api.put(`/admin-users/${selectedUser.id}/global-permissions`, {
                globalPermissions,
            });
            const updatedGlobalPermissions = Array.isArray(data?.globalPermissions) ? data.globalPermissions : globalPermissions;
            setGlobalPermissions(updatedGlobalPermissions);
            setUsers(prev => prev.map(user => (
                user.id === selectedUser.id ? { ...user, globalPermissions: updatedGlobalPermissions } : user
            )));
            toast.success("Permisos globales actualizados.", {
                description: "Si el usuario tiene sesión activa, debe volver a iniciar sesión.",
            });
            if (onPermissionsUpdated) onPermissionsUpdated();
        } catch (error: any) {
            const code = error.response?.data?.code;
            toast.error(
                code === "GLOBAL_PERMISSION_NOT_ALLOWED"
                    ? "Ese permiso no puede asignarse con alcance global."
                    : "Error al actualizar permisos globales.",
            );
        } finally {
            setIsSavingGlobalPermissions(false);
        }
    };

    const handleAddAssignment = async () => {
        if (newAssignment.eventIds.length === 0 || newAssignment.permissions.length === 0) {
            toast.error("Debe seleccionar al menos un evento y un permiso.");
            return;
        }

        try {
            setIsAddingAssignment(true);
            await Promise.all(
                newAssignment.eventIds.map((eventId) =>
                    api.post("/admin-users/assign", {
                        adminId: selectedUser?.id,
                        eventId,
                        roleName: newAssignment.roleName,
                        permissions: newAssignment.permissions,
                    }),
                ),
            );
            toast.success("Permisos asignados correctamente.");
            setIsAddingAssignment(false);
            setNewAssignment({ eventIds: [], roleName: "gestor_registros", permissions: ROLE_PRESETS.find(r => r.id === "gestor_registros")?.perms || [] });
            // Refresh assignments
            if (selectedUser) openAssignments(selectedUser);
            fetchUsers(); // Update count
            if (onPermissionsUpdated) onPermissionsUpdated();
        } catch (error) {
            setIsAddingAssignment(false);
            toast.error("Error al asignar permisos");
        }
    };

    const handleDeleteAssignment = async (eventId: string) => {
        if (!selectedUser) return;
        try {
            await api.delete(`/admin-users/${selectedUser.id}/assign/${eventId}`);
            setAssignments(prev => prev.filter(a => a.eventId !== eventId));
            toast.success("Asignación eliminada.");
            fetchUsers(); // Update count
            if (onPermissionsUpdated) onPermissionsUpdated();
        } catch (error) {
            toast.error("Error al eliminar asignación");
        }
    };

    const handleEditAssignmentLoading = (assignment: Assignment) => {
        setNewAssignment({
            eventIds: [assignment.eventId],
            roleName: assignment.roleName,
            permissions: assignment.permissions
        });
        toast.info(`Cargado: ${assignment.event.name} para edición.`);
    };

    const normalizedSearch = searchTerm.toLowerCase();
    const filteredUsers = users.filter(u => {
        const safeName = (u.name || '').toLowerCase();
        const safeEmail = (u.email || '').toLowerCase();
        return safeName.includes(normalizedSearch) || safeEmail.includes(normalizedSearch);
    });

    return (
        <div className="space-y-6 animate-in fade-in-50 duration-500">

            {/* Header Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-gradient-to-br from-[#0f2744] to-[#1e3a5f] text-white overflow-hidden relative">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="h-5 w-5 text-[#c9a227]" />
                            Usuarios Totales
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-4xl font-bold">{users.length}</div>
                        <p className="text-sm text-blue-200 mt-2">Administradores registrados en el sistema</p>
                    </CardContent>
                    <div className="absolute top-[-20px] right-[-20px] opacity-10">
                        <Users className="h-32 w-32" />
                    </div>
                </Card>

                <Card className="shadow-sm border-gray-200">
                    <CardHeader>
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wider">Activos</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold text-green-600">{users.filter(u => u.isActive).length}</div>
                    </CardContent>
                </Card>

                <Card className="shadow-sm border-gray-200">
                    <CardHeader>
                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wider">Super Admins</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-3xl font-bold text-[#c9a227]">{users.filter(u => u.isSuperAdmin).length}</div>
                    </CardContent>
                </Card>
            </div>

            <Card className="shadow-sm border-gray-200">
                <CardHeader className="p-6 border-b border-gray-100 flex flex-row items-center justify-between space-y-0">
                    <div>
                        <CardTitle className="text-xl font-bold text-[#0f2744]">Control de Acceso</CardTitle>
                        <CardDescription>Gestión de usuarios institucionales y sus permisos por evento.</CardDescription>
                    </div>
                    <div className="flex gap-4">
                        <div className="relative w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Buscar usuario..."
                                className="pl-10"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <Button className="bg-[#1e3a5f] text-white hover:bg-[#0f2744]" onClick={() => setIsRegisterOpen(true)}>
                            <UserPlus className="mr-2 h-4 w-4" />
                            Registrar Nuevo
                        </Button>
                    </div>
                </CardHeader>
                {isSuperAdmin && (
                    <div className="px-6 pt-6">
                        <Alert className="border-amber-200 bg-amber-50 text-amber-900">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription className="text-sm">
                                <span className="font-semibold">Las acciones de limpieza son de alto riesgo.</span>{" "}
                                El borrado físico solo estará disponible para datos de prueba autorizados por infraestructura.
                            </AlertDescription>
                        </Alert>
                    </div>
                )}
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-20 flex flex-col items-center justify-center text-gray-400">
                            <Loader2 className="h-8 w-8 animate-spin mb-4" />
                            <p>Cargando lista de usuarios...</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
                                    <TableHead className="w-[300px]">Usuario</TableHead>
                                    <TableHead>Proveedor</TableHead>
                                    <TableHead>Rol Principal</TableHead>
                                    <TableHead>Eventos</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead>Último Acceso</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-32 text-center text-gray-500">
                                            No se encontraron usuarios que coincidan con la búsqueda.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell className="py-4">
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-9 w-9 border-2 border-white shadow-sm ring-1 ring-gray-100">
                                                        <AvatarFallback className="bg-[#0f2744] text-white text-xs font-bold">
                                                            {user.firstName ? user.firstName[0] : user.name?.[0]}
                                                            {user.fatherLastName ? user.fatherLastName[0] : ""}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-[#0f2744] text-sm">
                                                            {user.firstName
                                                                ? `${user.firstName} ${user.fatherLastName || ''} ${user.motherLastName || ''}`.trim()
                                                                : user.name}
                                                        </span>
                                                        <span className="text-[10px] text-gray-500 font-medium uppercase tracking-tight">{user.authProvider}</span>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {user.isSuperAdmin ? (
                                                    <Badge className="bg-[#c9a227] text-white border-0">SuperAdmin</Badge>
                                                ) : (
                                                    <span className="text-sm text-gray-600">Admin/Gestor</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <span className="font-bold text-[#0f2744]">{user._count.assignedEvents}</span>
                                                    <span className="text-xs text-gray-500">asig.</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={user.isActive}
                                                        onCheckedChange={() => handleToggleStatus(user)}
                                                        disabled={user.isSuperAdmin && user.email === 'victor.gutierrez@unach.mx'}
                                                    />
                                                    <span className={`text-xs font-medium ${user.isActive ? 'text-green-600' : 'text-red-600'}`}>
                                                        {user.isActive ? 'Activo' : 'Inactivo'}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col text-xs text-gray-500">
                                                    <span>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Nunca'}</span>
                                                    {user.lastLoginAt && <span>{new Date(user.lastLoginAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className={`h-8 w-8 ${user.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                                                        onClick={() => handleToggleStatus(user)}
                                                        title={user.isActive ? "Desactivar Usuario" : "Activar Usuario"}
                                                    >
                                                        {user.isActive ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                                                        onClick={() => openAssignments(user)}
                                                        title="Gestionar Permisos"
                                                    >
                                                        <Settings className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-orange-600 hover:bg-orange-50"
                                                        onClick={() => openEdit(user)}
                                                        title="Editar Usuario"
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    {isSuperAdmin && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-blue-700 hover:bg-blue-50"
                                                            onClick={() => openDependencies(user)}
                                                            title="Ver dependencias"
                                                        >
                                                            <ShieldCheck className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-red-600 hover:bg-red-50"
                                                        onClick={() => handleDeleteUser(user)}
                                                        title="Eliminar Acceso"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* ASSIGNMENTS MODAL */}
            <Dialog open={isAssignmentsOpen} onOpenChange={setIsAssignmentsOpen}>
                <DialogContent className="max-w-3xl bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield className="h-5 w-5 text-[#c9a227]" />
                            Permisos de Administrador
                        </DialogTitle>
                        <DialogDescription>
                            Gestiona a qué eventos tiene acceso <strong>{selectedUser?.name}</strong>.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1 py-1">
                        {/* HEADER DE USUARIO EN MODAL */}
                        {selectedUser && (
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                <div className="h-10 w-10 rounded-full bg-[#0f2744] text-white flex items-center justify-center font-bold">
                                    {selectedUser.name?.charAt(0)}
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-bold text-gray-900">{selectedUser.name}</p>
                                    <p className="text-xs text-gray-500 lowercase">{selectedUser.email}</p>
                                </div>
                            </div>
                        )}

                        {/* PERMISOS GLOBALES DELEGADOS */}
                        <div className="space-y-3 p-4 rounded-xl border border-blue-100 bg-blue-50/40">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="space-y-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck className="h-4 w-4 text-blue-700" />
                                        <h4 className="text-sm font-bold text-blue-950">Privilegios Globales Delegados</h4>
                                    </div>
                                    <p className="text-xs text-blue-900 leading-relaxed">
                                        Aplican a todos los eventos solo para las acciones seleccionadas. No otorgan acceso total de SuperAdmin.
                                    </p>
                                </div>
                                <Badge variant="outline" className="w-fit border-blue-200 bg-white text-blue-800">
                                    {globalPermissions.length} activos
                                </Badge>
                            </div>

                            {selectedUser?.isSuperAdmin ? (
                                <Alert className="border-yellow-200 bg-yellow-50">
                                    <Lock className="h-4 w-4 text-yellow-700" />
                                    <AlertDescription className="text-xs text-yellow-900">
                                        Este usuario ya es SuperAdmin. Los permisos globales delegados se gestionan para usuarios no SuperAdmin.
                                    </AlertDescription>
                                </Alert>
                            ) : (
                                <div className="space-y-4">
                                    {isLoadingAssignments ? (
                                        <div className="flex justify-center p-4"><Loader2 className="h-5 w-5 animate-spin text-blue-900" /></div>
                                    ) : (
                                        GLOBAL_PERMISSION_CATEGORIES.map(cat => (
                                            <div key={`global-${cat.id}`} className="space-y-2">
                                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-blue-950">
                                                    {cat.icon}
                                                    {cat.name}
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {cat.permissions.map(perm => {
                                                        const isSelected = globalPermissions.includes(perm.id);
                                                        return (
                                                            <button
                                                                key={`global-${perm.id}`}
                                                                type="button"
                                                                onClick={() => toggleGlobalPermission(perm.id)}
                                                                className={`text-left flex items-start gap-3 rounded-lg border p-3 transition-colors ${isSelected
                                                                    ? "border-blue-500 bg-white shadow-sm"
                                                                    : "border-blue-100 bg-white/70 hover:bg-white"
                                                                    }`}
                                                            >
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    className="mt-0.5 pointer-events-none"
                                                                />
                                                                <span className="min-w-0">
                                                                    <span className="block text-xs font-bold text-slate-900 break-words">{perm.name}</span>
                                                                    <span className="block text-[11px] leading-relaxed text-slate-600 break-words">{perm.desc}</span>
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))
                                    )}

                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-t border-blue-100 pt-3">
                                        <p className="text-[11px] text-blue-900 leading-relaxed">
                                            Solo las acciones mostradas pueden delegarse globalmente. Pago Manual y sincronización SYSWEB son excepciones financieras explícitas; no habilitan otros permisos.
                                        </p>
                                        <Button
                                            onClick={handleSaveGlobalPermissions}
                                            disabled={isSavingGlobalPermissions || isLoadingAssignments}
                                            className="bg-blue-700 hover:bg-blue-800 text-white"
                                        >
                                            {isSavingGlobalPermissions ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                                            Guardar globales
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* LISTADO DE EVENTOS ACTUALES (Mini tarjetas) - AHORA ARRIBA */}
                        <div className="space-y-3 pt-2">
                            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Eventos Asignados Actualmente</h4>
                            {isLoadingAssignments ? (
                                <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
                            ) : assignments.length === 0 ? (
                                <p className="text-sm text-gray-400 italic text-center py-4 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">Sin eventos asignados todavía.</p>
                            ) : (
                                <div className="space-y-2">
                                    {assignments.map(a => (
                                        <div key={a.eventId} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:border-blue-200 transition-colors group">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                                                    <Calendar className="h-4 w-4 text-blue-900" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-gray-800">{a.event.name}</p>
                                                    <div className="flex gap-1 flex-wrap mt-1">
                                                        {a.permissions.map((p: string) => (
                                                            <Badge key={p} variant="secondary" className="text-[9px] px-1 py-0 bg-gray-100 text-gray-600 border-none font-normal">
                                                                {p.replace('can_', '').replace('_', ' ')}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-blue-400 hover:text-blue-600 hover:bg-blue-50"
                                                    onClick={() => handleEditAssignmentLoading(a)}
                                                    title="Editar permisos"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => handleDeleteAssignment(a.eventId)}
                                                    title="Eliminar asignación"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-gray-100 my-2" />

                        {/* SECCIÓN DE ASIGNACIÓN */}
                        <div className="space-y-4 pt-2">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Eventos Disponibles</label>
                                    <Select
                                        value={newAssignment.eventIds[0] || ""}
                                        onValueChange={(val) => setNewAssignment({ ...newAssignment, eventIds: [val] })}
                                    >
                                        <SelectTrigger className="bg-white">
                                            <SelectValue placeholder="Seleccionar evento..." />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            {events.filter(e =>
                                                !assignments.some(a => a.eventId === e.id) ||
                                                newAssignment.eventIds.includes(e.id)
                                            ).map(e => (
                                                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Rol Sugerido</label>
                                    <Select
                                        value={newAssignment.roleName}
                                        onValueChange={handleApplyPreset}
                                    >
                                        <SelectTrigger className="bg-white border-blue-100 ring-blue-50">
                                            <SelectValue placeholder="Seleccionar rol..." />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            {ROLE_PRESETS.map(role => (
                                                <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* CATEGORÍAS DE PERMISOS */}
                            <div className="space-y-6">
                                {PERMISSION_CATEGORIES.map(cat => (
                                    <div key={cat.id} className="space-y-3">
                                        <div className="flex items-center justify-between px-1">
                                            <div className="flex items-center gap-2">
                                                <div className="p-1.5 bg-blue-50 text-blue-900 rounded-lg">
                                                    {cat.icon}
                                                </div>
                                                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">{cat.name}</span>
                                            </div>
                                            <Badge variant="secondary" className="text-[10px] bg-gray-100 text-gray-500 border-none font-bold">
                                                {cat.permissions.filter(p => newAssignment.permissions.includes(p.id)).length} / {cat.permissions.length}
                                            </Badge>
                                        </div>

                                        <div className="grid grid-cols-1 gap-2">
                                            {cat.permissions.map(perm => {
                                                const isSelected = newAssignment.permissions.includes(perm.id);
                                                return (
                                                    <div
                                                        key={perm.id}
                                                        onClick={() => togglePermission(perm.id)}
                                                        className={`flex items-start gap-4 p-4 rounded-xl border transition-all cursor-pointer group ${isSelected
                                                            ? 'bg-blue-50/40 border-blue-600 shadow-sm ring-1 ring-blue-600/10'
                                                            : 'bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'
                                                            }`}
                                                    >
                                                        <div className="flex items-center h-5">
                                                            <Checkbox
                                                                id={perm.id}
                                                                checked={isSelected}
                                                                onCheckedChange={() => {
                                                                    // El toggle ya se maneja por el onClick del div padre
                                                                }}
                                                                className={`h-5 w-5 border-gray-300 transition-colors pointer-events-none ${isSelected ? 'border-blue-600 bg-blue-600 text-white' : ''
                                                                    }`}
                                                            />
                                                        </div>
                                                        <div className="space-y-1 select-none flex-1">
                                                            <Label
                                                                htmlFor={perm.id}
                                                                className={`text-sm font-bold cursor-pointer transition-colors block ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}
                                                            >
                                                                {perm.name}
                                                            </Label>
                                                            <p className="text-xs text-gray-500 leading-relaxed font-medium">
                                                                {perm.desc}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Button
                                className="w-full bg-[#c9a227] hover:bg-[#b08d20] text-blue-950 font-bold"
                                onClick={handleAddAssignment}
                                disabled={isAddingAssignment}
                            >
                                {isAddingAssignment ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Plus className="w-4 h-4 mr-2" />
                                )}
                                {isAddingAssignment ? "Asignando..." : "Asignar Permisos al Evento"}
                            </Button>
                        </div>



                        {selectedUser?.isSuperAdmin && (
                            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 flex items-start gap-3">
                                <Lock className="h-5 w-5 text-[#c9a227] mt-0.5" />
                                <div className="text-xs text-yellow-800">
                                    <p className="font-bold">Usuario SuperAdmin</p>
                                    <p>Los SuperAdministradores tienen permisos globales inherentes ('*') y no requieren asignaciones por evento para acceder a los módulos.</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsAssignmentsOpen(false)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* REGISTER MODAL */}
            <Dialog open={isRegisterOpen} onOpenChange={setIsRegisterOpen}>
                <DialogContent className="max-w-md bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[#0f2744]">
                            <UserPlus className="h-5 w-5 text-[#1e3a5f]" />
                            Registrar Administrador
                        </DialogTitle>
                        <DialogDescription>
                            Busque un usuario institucional por su correo @unach.mx para autorizar su acceso.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Proveedor de Identidad</label>
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button
                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${regAuthProvider === 'LDAP' ? 'bg-white shadow text-blue-900 border border-blue-100' : 'text-gray-500'}`}
                                    onClick={() => setRegAuthProvider('LDAP')}
                                >
                                    LDAP Institucional
                                </button>
                                {isDeveloper && (
                                    <button
                                        className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${regAuthProvider === 'LOCAL' ? 'bg-white shadow text-blue-900 border border-blue-100' : 'text-gray-500'}`}
                                        onClick={() => setRegAuthProvider('LOCAL')}
                                    >
                                        Local (Pruebas)
                                    </button>
                                )}
                            </div>
                        </div>

                        {regAuthProvider === "LDAP" ? (
                            <div className="flex gap-2">
                                <Input
                                    placeholder="ejemplo@unach.mx"
                                    value={searchEmail}
                                    onChange={(e) => setSearchEmail(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearchLdap()}
                                />
                                <Button variant="outline" onClick={handleSearchLdap} disabled={isSearchingLdap}>
                                    {isSearchingLdap ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4 animate-in slide-in-from-bottom-2">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Correo Electrónico</label>
                                    <Input placeholder="usuario.local@unach.mx" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)} />
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase">Nombre(s)</label>
                                        <Input value={localNames.first} onChange={(e) => setLocalNames({ ...localNames, first: e.target.value })} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-gray-500 uppercase">Apellido Paterno</label>
                                            <Input value={localNames.father} onChange={(e) => setLocalNames({ ...localNames, father: e.target.value })} />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-gray-500 uppercase">Apellido Materno</label>
                                            <Input value={localNames.mother} onChange={(e) => setLocalNames({ ...localNames, mother: e.target.value })} />
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Contraseña</label>
                                    <div className="relative">
                                        <Input
                                            type={showPassReg ? "text" : "password"}
                                            placeholder="Min. 6 caracteres"
                                            value={localPass}
                                            onChange={(e) => setLocalPass(e.target.value)}
                                        />
                                        {isDeveloper && (
                                            <button
                                                type="button"
                                                onClick={() => setShowPassReg(!showPassReg)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                {showPassReg ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {((regAuthProvider === "LDAP" && ldapQueryResult) || regAuthProvider === "LOCAL") && (
                            <div className="p-4 bg-primary/5 rounded-xl border border-primary/10 space-y-3 animate-in fade-in zoom-in-95 duration-200">
                                {regAuthProvider === "LDAP" && (
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-white border border-blue-200 flex items-center justify-center font-bold text-[#1e3a5f]">
                                            {ldapQueryResult.fullName.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">{ldapQueryResult.fullName}</p>
                                            <p className="text-xs text-blue-600 font-medium lowercase italic">{ldapQueryResult.email}</p>
                                        </div>
                                    </div>
                                )}

                                {isDeveloper && (
                                    <div className="flex items-center space-x-2 pt-2 border-t border-primary/10">
                                        <Checkbox
                                            id="super-admin-reg"
                                            checked={registerAsSuperAdmin}
                                            onCheckedChange={(c: any) => setRegisterAsSuperAdmin(!!c)}
                                        />
                                        <label htmlFor="super-admin-reg" className="text-xs font-bold text-[#c9a227] cursor-pointer">
                                            Asignar rol de SuperAdmin
                                        </label>
                                    </div>
                                )}

                                <Button className="w-full bg-[#1e3a5f] text-white hover:bg-[#0f2744] mt-2" onClick={handleRegisterUser}>
                                    Confirmar Registro
                                </Button>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsRegisterOpen(false)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* EDIT MODAL */}
            <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                <DialogContent className="max-w-md bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[#0f2744]">
                            <Pencil className="h-5 w-5 text-orange-600" />
                            Editar Administrador
                        </DialogTitle>
                        <DialogDescription>
                            Modifique los datos básicos del usuario.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-gray-500 uppercase">Correo (No editable)</label>
                            <Input value={editForm.email} disabled className="bg-gray-50" />
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-gray-500 uppercase">Nombre(s)</label>
                            <Input value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-500 uppercase">Apellido Paterno</label>
                                <Input value={editForm.fatherLastName} onChange={(e) => setEditForm({ ...editForm, fatherLastName: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-gray-500 uppercase">Apellido Materno</label>
                                <Input value={editForm.motherLastName} onChange={(e) => setEditForm({ ...editForm, motherLastName: e.target.value })} />
                            </div>
                        </div>

                        {isDeveloper && (
                            <div className="space-y-2 bg-blue-50/50 p-3 rounded-lg border border-blue-100/50">
                                <label className="text-[10px] font-bold text-blue-600 uppercase">Reset Contraseña (Solo Local)</label>
                                <div className="relative">
                                    <Input
                                        type={showPassEdit ? "text" : "password"}
                                        placeholder="Nueva contraseña (dejar vacío para no cambiar)"
                                        value={editForm.password}
                                        onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassEdit(!showPassEdit)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassEdit ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                <div className="flex items-center space-x-2 pt-2">
                                    <Checkbox
                                        id="edit-super-admin"
                                        checked={editForm.isSuperAdmin}
                                        onCheckedChange={(c: any) => setEditForm({ ...editForm, isSuperAdmin: !!c })}
                                    />
                                    <label htmlFor="edit-super-admin" className="text-xs font-bold text-[#c9a227] cursor-pointer">
                                        Es SuperAdministrador
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
                        <Button className="bg-[#0f2744] text-white hover:bg-[#1e3a5f]" onClick={handleEditUser}>Guardar Cambios</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CleanupDiagnosisSheet
                open={isDiagnosisOpen}
                onOpenChange={setIsDiagnosisOpen}
                target={diagnosisTarget}
                diagnosis={diagnosis}
                loading={isLoadingDiagnosis}
                actionLoading={isCleanupLoading}
                onDeactivate={deactivateFromDiagnosis}
                onDryRun={runDryRun}
            />

            <CleanupDryRunDialog
                open={isDryRunOpen}
                onOpenChange={setIsDryRunOpen}
                target={diagnosisTarget}
                dryRun={dryRun}
                onContinue={() => {
                    setCleanupConfirmation("");
                    setIsConfirmCleanupOpen(true);
                }}
            />

            <Dialog open={isConfirmCleanupOpen} onOpenChange={setIsConfirmCleanupOpen}>
                <DialogContent className="max-w-2xl bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="h-5 w-5" />
                            Confirmar limpieza de prueba
                        </DialogTitle>
                        <DialogDescription>{diagnosisTarget?.name}</DialogDescription>
                    </DialogHeader>
                    <Alert className="border-red-200 bg-red-50 text-red-800">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            Esta acción eliminará físicamente datos de prueba. El plan será recalculado antes de aplicar la limpieza.
                        </AlertDescription>
                    </Alert>
                    <div className="grid grid-cols-2 gap-4 rounded-lg border p-4 text-sm">
                        <div>
                            <p className="text-gray-500">Usuario</p>
                            <p className="font-semibold text-[#0f2744]">{diagnosisTarget?.name}</p>
                        </div>
                        <div>
                            <p className="text-gray-500">Filas estimadas</p>
                            <p className="font-semibold text-[#0f2744]">{dryRun?.plan.reduce((sum, item) => sum + item.count, 0) || 0}</p>
                        </div>
                        <div>
                            <p className="text-gray-500">Allowlist</p>
                            <Badge className={dryRun?.allowlisted ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                                {dryRun?.allowlisted ? "Sí" : "No"}
                            </Badge>
                        </div>
                        <div>
                            <p className="text-gray-500">Bloqueadores duros</p>
                            <Badge className={dryRun?.hardDeleteBlockers.length ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                                {dryRun?.hardDeleteBlockers.length || 0}
                            </Badge>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Escribe LIMPIAR PRUEBA para continuar.</Label>
                        <Input
                            value={cleanupConfirmation}
                            onChange={(e) => setCleanupConfirmation(e.target.value)}
                            placeholder="LIMPIAR PRUEBA"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsConfirmCleanupOpen(false)}>Cancelar</Button>
                        <Button
                            className="bg-red-600 text-white hover:bg-red-700"
                            disabled={cleanupConfirmation !== "LIMPIAR PRUEBA" || isCleanupLoading}
                            onClick={applyCleanup}
                        >
                            {isCleanupLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Limpiar datos de prueba
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CleanupResultDialog
                open={isCleanupResultOpen}
                onOpenChange={setIsCleanupResultOpen}
                target={diagnosisTarget}
                result={cleanupResult}
                onRefresh={fetchUsers}
            />
        </div >
    );
}

function riskBadgeClass(risk?: string) {
    const base = "max-w-full whitespace-normal break-words text-left leading-tight";
    if (risk === "CLEANUP_CANDIDATE") return `${base} bg-green-100 text-green-700 border-green-200`;
    if (risk === "BLOCKED") return `${base} bg-red-100 text-red-700 border-red-200`;
    return `${base} bg-amber-100 text-amber-700 border-amber-200`;
}

function dependencyLabel(key: string) {
    const labels: Record<string, string> = {
        adminAssignments: "Asignaciones administrativas",
        eventMembers: "Miembros de evento",
        eventInvites: "Invitaciones",
        evaluatorAssignments: "Asignaciones de evaluador",
        evaluationDecisions: "Evaluaciones",
        submittedEvaluations: "Evaluaciones enviadas",
        eventRegistrations: "Registros de evento",
        paidRegistrations: "Registros pagados",
        paymentSyncJobs: "Jobs SYSWEB",
        recognitionLogs: "Reconocimientos",
        recognitionSent: "Reconocimientos SENT",
        auditLogs: "AuditLogs asociados",
        emailOutbox: "Correos outbox",
    };
    return labels[key] || key;
}

function CleanupDiagnosisSheet({
    open,
    onOpenChange,
    target,
    diagnosis,
    loading,
    actionLoading,
    onDeactivate,
    onDryRun,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    target: AdminUser | null;
    diagnosis: CleanupDiagnosis | null;
    loading: boolean;
    actionLoading: boolean;
    onDeactivate: () => void;
    onDryRun: () => void;
}) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full overflow-y-auto bg-white px-5 py-6 sm:max-w-2xl sm:px-7">
                <SheetHeader className="space-y-2 pl-1 pr-8 text-left">
                    <SheetTitle className="break-words text-2xl text-[#0f2744]">Diagnóstico de dependencias</SheetTitle>
                    <SheetDescription>{target?.name}</SheetDescription>
                </SheetHeader>

                {loading ? (
                    <div className="flex h-72 items-center justify-center text-gray-500">
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Cargando diagnóstico...
                    </div>
                ) : diagnosis ? (
                    <div className="mt-6 space-y-6 pl-1">
                        <div className="flex flex-wrap gap-2 pr-4">
                            <Badge className="bg-violet-100 text-violet-700">SuperAdmin only</Badge>
                            <Badge className="bg-red-100 text-red-700">Alto riesgo</Badge>
                            <Badge className="bg-blue-100 text-blue-700">Solo diagnóstico</Badge>
                        </div>

                        <div className="grid grid-cols-1 gap-4 rounded-lg border p-4 text-sm sm:grid-cols-2">
                            <div className="min-w-0">
                                <p className="text-gray-500">Usuario</p>
                                <p className="break-words font-semibold text-[#0f2744]">{diagnosis.user.name || target?.name}</p>
                            </div>
                            <div className="min-w-0">
                                <p className="text-gray-500">Estado</p>
                                <Badge className={diagnosis.user.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                                    {diagnosis.user.isActive ? "Activo" : "Inactivo"}
                                </Badge>
                            </div>
                            <div className="min-w-0">
                                <p className="text-gray-500">Correo</p>
                                <p className="break-all font-semibold">{diagnosis.user.emailMasked}</p>
                            </div>
                            <div className="min-w-0">
                                <p className="text-gray-500">Allowlist</p>
                                <Badge className={diagnosis.allowlisted ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                                    {diagnosis.allowlisted ? "Sí" : "No"}
                                </Badge>
                            </div>
                        </div>

                        <section className="space-y-3">
                            <h3 className="font-bold text-[#0f2744]">1. Clasificación</h3>
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                                <Badge className={riskBadgeClass(diagnosis.riskLevel)}>{diagnosis.riskLevel}</Badge>
                                <p className="mt-2 text-sm text-amber-900">
                                    {diagnosis.canHardDelete
                                        ? "El usuario califica para dry-run de limpieza de prueba."
                                        : "El usuario tiene relaciones que deben conservarse o no está autorizado para borrado físico."}
                                </p>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    <Button
                                        className="bg-orange-600 text-white hover:bg-orange-700"
                                        disabled={!diagnosis.canDeactivate || actionLoading}
                                        onClick={onDeactivate}
                                    >
                                        {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Desactivar usuario
                                    </Button>
                                    <Button
                                        className="min-w-fit"
                                        disabled={!diagnosis.canHardDelete || actionLoading}
                                        onClick={onDryRun}
                                    >
                                        <Database className="mr-2 h-4 w-4" />
                                        Ejecutar dry-run
                                    </Button>
                                </div>
                            </div>
                        </section>

                        <section className="space-y-3">
                            <h3 className="font-bold text-[#0f2744]">2. Bloqueadores duros</h3>
                            <div className={`rounded-lg border p-3 ${diagnosis.hardDeleteBlocked ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}`}>
                                {diagnosis.hardDeleteBlockers.length ? (
                                    diagnosis.hardDeleteBlockers.map((blocker) => (
                                        <div key={blocker} className="flex items-center justify-between gap-3 border-b border-red-100 py-2 last:border-0">
                                            <span className="min-w-0 break-words text-sm text-red-800">{blocker}</span>
                                            <Badge className="shrink-0 bg-red-100 text-red-700">Sí</Badge>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-green-800">No se detectaron bloqueadores duros.</p>
                                )}
                            </div>
                        </section>

                        <section className="space-y-3">
                            <h3 className="font-bold text-[#0f2744]">3. Dependencias detectadas</h3>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {Object.entries(diagnosis.dependencies).map(([key, value]) => (
                                    <div key={key} className="min-w-0 rounded-lg border p-3">
                                        <p className="break-words text-xs font-semibold text-gray-600">{dependencyLabel(key)}</p>
                                        <p className="mt-2 text-2xl font-bold text-[#0f2744]">{value}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="space-y-3">
                            <h3 className="font-bold text-[#0f2744]">4. Elegibilidad de correo</h3>
                            <div className="flex gap-3 rounded-lg border p-4">
                                <MailIcon className="h-6 w-6 shrink-0 text-blue-600" />
                                <div className="min-w-0">
                                    <p className="font-semibold">Correos futuros</p>
                                    <p className="break-words text-sm text-gray-600">{diagnosis.mailEligibility.reason}</p>
                                </div>
                                <Badge className={diagnosis.mailEligibility.canReceiveOperationalEmails ? "ml-auto shrink-0 bg-green-100 text-green-700" : "ml-auto shrink-0 bg-gray-100 text-gray-700"}>
                                    {diagnosis.mailEligibility.canReceiveOperationalEmails ? "Sí" : "No"}
                                </Badge>
                            </div>
                        </section>

                        <section className="space-y-3">
                            <h3 className="font-bold text-[#0f2744]">5. Recomendación</h3>
                            <Alert className="border-amber-200 bg-amber-50 text-amber-900">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>{diagnosis.recommendation}</AlertDescription>
                            </Alert>
                        </section>
                    </div>
                ) : (
                    <Alert className="mt-6 border-red-200 bg-red-50 text-red-800">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>No se pudo cargar el diagnóstico.</AlertDescription>
                    </Alert>
                )}

                <SheetFooter className="mt-6 pl-1">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

function CleanupDryRunDialog({
    open,
    onOpenChange,
    target,
    dryRun,
    onContinue,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    target: AdminUser | null;
    dryRun: CleanupDryRun | null;
    onContinue: () => void;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-white p-6 sm:p-7">
                <DialogHeader className="pr-8 text-left">
                    <DialogTitle className="flex items-start gap-2 break-words text-[#0f2744]">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                        <span>Dry-run de limpieza de prueba</span>
                    </DialogTitle>
                    <DialogDescription>{target?.name}</DialogDescription>
                </DialogHeader>
                <Alert className="border-blue-200 bg-blue-50 text-blue-800">
                    <AlertDescription>El sistema simuló la limpieza. No se modificó la base de datos.</AlertDescription>
                </Alert>
                {dryRun && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <div className="min-w-0 rounded-lg border p-3">
                                <p className="text-xs text-gray-500">Allowlist</p>
                                <p className="font-bold">{dryRun.allowlisted ? "Sí" : "No"}</p>
                            </div>
                            <div className="min-w-0 rounded-lg border p-3">
                                <p className="text-xs text-gray-500">Bloqueadores duros</p>
                                <p className="font-bold">{dryRun.hardDeleteBlockers.length}</p>
                            </div>
                            <div className="min-w-0 rounded-lg border p-3">
                                <p className="text-xs text-gray-500">Riesgo</p>
                                <Badge className={riskBadgeClass(dryRun.riskLevel)}>{dryRun.riskLevel.replace(/_/g, " ")}</Badge>
                            </div>
                            <div className="min-w-0 rounded-lg border p-3">
                                <p className="text-xs text-gray-500">Acción permitida</p>
                                <p className="break-words font-bold leading-snug">{dryRun.canApply ? "Limpieza de prueba" : "No aplicable"}</p>
                            </div>
                        </div>
                        <div className="overflow-x-auto rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="min-w-[220px]">Entidad</TableHead>
                                        <TableHead className="w-[120px]">Acción</TableHead>
                                        <TableHead className="w-[120px] text-right">Filas afectadas</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {dryRun.plan.map((item) => (
                                        <TableRow key={item.entity}>
                                            <TableCell className="whitespace-normal break-words">{item.entity}</TableCell>
                                            <TableCell><Badge variant="outline">{item.action}</Badge></TableCell>
                                            <TableCell className="text-right">{item.count}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        <Alert className="border-blue-200 bg-blue-50 text-blue-800">
                            <AlertDescription>Este plan será recalculado nuevamente antes de aplicar la limpieza.</AlertDescription>
                        </Alert>
                    </div>
                )}
                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                    <Button disabled={!dryRun?.canApply} onClick={onContinue}>Continuar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CleanupResultDialog({
    open,
    onOpenChange,
    target,
    result,
    onRefresh,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    target: AdminUser | null;
    result: CleanupApplyResult | null;
    onRefresh: () => void;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-white">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-green-700">
                        <CheckCircle2 className="h-5 w-5" />
                        Limpieza registrada
                    </DialogTitle>
                    <DialogDescription>{target?.name}</DialogDescription>
                </DialogHeader>
                <Alert className="border-green-200 bg-green-50 text-green-800">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>La limpieza de prueba fue aplicada y quedó registrada en auditoría.</AlertDescription>
                </Alert>
                {result && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-lg border p-4">
                                <p className="text-xs text-gray-500">Entidades limpiadas</p>
                                <p className="text-2xl font-bold text-[#0f2744]">{Object.values(result.deletedCounts).filter(Boolean).length}</p>
                            </div>
                            <div className="rounded-lg border p-4">
                                <p className="text-xs text-gray-500">Filas eliminadas</p>
                                <p className="text-2xl font-bold text-[#0f2744]">{result.totalDeleted}</p>
                            </div>
                            <div className="rounded-lg border p-4">
                                <p className="text-xs text-gray-500">Bloqueadores al aplicar</p>
                                <p className="text-2xl font-bold text-[#0f2744]">{result.hardDeleteBlockers.length}</p>
                            </div>
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Entidad</TableHead>
                                    <TableHead className="text-right">Filas eliminadas</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {Object.entries(result.deletedCounts).map(([key, value]) => (
                                    <TableRow key={key}>
                                        <TableCell>{dependencyLabel(key)}</TableCell>
                                        <TableCell className="text-right">{value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
                    <Button onClick={onRefresh}>Actualizar usuarios</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
